import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserRound, Mail, Phone, Building2, Loader2 } from "lucide-react";

/**
 * ProfileDialog — lets any user (staff, technician, admin, student) update
 * their own name, mobile number, and official email ID.
 *
 * Props:
 *   open  {boolean}  — controlled open state
 *   onOpenChange {fn} — callback to toggle open state
 */
export default function ProfileDialog({ open, onOpenChange }) {
  const { user, updateProfile } = useAuth();

  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [dept, setDept]   = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Sync form fields whenever dialog opens or user changes
  useEffect(() => {
    if (open && user) {
      setName(user.name  || "");
      setPhone(user.phone || "");
      setEmail(user.email || "");
      setDept(user.department || "");
      setErrors({});
    }
  }, [open, user]);

  const validate = () => {
    const e = {};
    if (!name.trim())              e.name  = "Name is required.";
    if (name.trim().length > 120)  e.name  = "Name must be 120 chars or fewer.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
                                   e.email = "Enter a valid email address.";
    if (phone && phone.trim().length > 32)
                                   e.phone = "Phone must be 32 chars or fewer.";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSaving(true);
    try {
      const payload = { name: name.trim() };
      // Only include phone / email / department when the user supplied a value.
      // The backend rejects null/empty phone ("required"), and undefined is
      // stripped from JSON — so we omit the key entirely when the field is blank,
      // meaning the backend leaves the existing value unchanged.
      if (phone.trim()) payload.phone = phone.trim();
      if (email.trim()) payload.email = email.trim();
      if (dept.trim()) payload.department = dept.trim();

      await updateProfile(payload);
      toast.success("Profile updated successfully.");
      onOpenChange(false);
    } catch (err) {
      const msg = err?.message || "Could not update profile.";
      if (msg.toLowerCase().includes("email")) {
        setErrors({ email: msg });
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-lg font-extrabold tracking-tight">
            <UserRound className="h-5 w-5 text-cobalt-500" />
            Edit Profile
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Update your name, mobile number, or official email ID.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Full Name */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-name" className="text-sm font-medium">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="profile-name"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }}
                placeholder="Your full name"
                className="pl-9"
                autoFocus
                maxLength={120}
              />
            </div>
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          {/* Mobile Number */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-phone" className="text-sm font-medium">
              Mobile Number
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: undefined })); }}
                placeholder="+91 98765 43210"
                className="pl-9"
                maxLength={32}
              />
            </div>
            {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
          </div>

          {/* Official Email */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-email" className="text-sm font-medium">
              Official Email ID
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="profile-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
                placeholder="you@institution.edu"
                className="pl-9"
              />
            </div>
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            <p className="text-[11px] text-muted-foreground">
              This email is used for ticket notifications. Must be unique across users.
            </p>
          </div>

          {/* Department — shown only if user already has one, or for non-student roles */}
          {(dept || (user?.user_type === "staff") || ["admin","technician","authority"].includes(user?.role)) && (
            <div className="space-y-1.5">
              <Label htmlFor="profile-dept" className="text-sm font-medium">
                Department / Unit
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="profile-dept"
                  value={dept}
                  onChange={(e) => setDept(e.target.value)}
                  placeholder="e.g. Computer Science"
                  className="pl-9"
                  maxLength={120}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-cobalt-500 hover:bg-cobalt-600 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
